import dayjs from "dayjs";
import { AnalyticsRecord, AnalyticsDiff } from "../app/slice/analyticsSlice";
import { PageNames } from "../app/slice/appSlice.model";
import { PageType } from "@reconvert/reconvert-utils";
import { TFunction, t } from "i18next";
import moment from "moment";

export enum IFramePostMessageType {
  UPDATE_BREADCRUMB = "update-breadcrumb",
  SHOW_SAVE_BAR = "show-save-bar",
  HIDE_SAVE_BAR = "hide-save-bar",
  OPEN_QUICK_SLOT_SETUP_MODAL = "open-quick-slot-setup-modal",
  OPEN_HELP_MODAL = "open-help-modal",
  OPEN_FLOW_EDITOR = "open-flow-editor",
  OPEN_AI_CHATBOT = "open-ai-chatbot",
  SYNC_SLOT_STATUS = "sync-slot-status",
  REDIRECT_TO_REVIEW_COLLECTOR_MODAL = "redirect-to-review-collector-modal",
  SET_IS_ELIGIBLE_FOR_REVIEW = "set-is-eligible-for-review",
  UPDATE_AI_CHAT_CONTEXT = 'update-ai-chat-context',
  /** Send user's product/collection selection back to the AI chatbot after missing-entities modal. */
  MISSING_ENTITIES_RESPONSE = "missing-entities-response",
  OPEN_PRE_MADE_TEMPLATE_MODAL = "open-pre-made-template-modal",
  CLOSE_HELP_MODAL = "close-help-modal",
  OPEN_RESOURCE_PICKER = "open-shopify-resource-picker",
  OPEN_CONFIRM_MODAL = "open-confirm-modal",
  /** Conditions notifies parent that onConfirm completed; parent should close modal */
  CONFIRM_FINISHED = "confirm-finished",
  CLOSE_PRE_MADE_TEMPLATE_MODAL = "close-pre-made-template-modal",
  CREATE_SLOT_WITH_TEMPLATE = "create-slot-with-template",
  ACTION_ACK = "action-ack",
}

export type ShopifyResourcePickerType = "product" | "collection" | "variant";

export enum IFramePostMessageEvent {
  HELP_VIDEO_SHOW = "help-video-show",
  HANDLE_SAVE = "handle-save",
  CLOSE_QUICK_SLOT_SETUP_MODAL = "close-quick-slot-setup-modal",
  CLOSE_HELP_MODAL = "close-help-modal",
  SYNC_SLOT_STATUS_RESPONSE = "sync-slot-status-response",
  SET_IS_ELIGIBLE_FOR_REVIEW_RESPONSE = "set-is-eligible-for-review-response",
  SHOPIFY_RESOURCE_PICKER_RESPONSE = "shopify-resource-picker-response",
  UPDATE_TOOL_OUTPUTS_ACTIONS = "update-tool-outputs-actions",
  OPEN_CONFIRM_MODAL_RESPONSE = "open-confirm-modal-response",
  /** Parent asks conditions to run onConfirm (async); conditions replies with CONFIRM_FINISHED when done */
  RUN_CONFIRM = "run-confirm",
}

export enum AIChatbotPostMessageEventType {
  SET_SELECTED_PAGE = "set-selected-page",
  SET_SELECTED_CONDITION = "set-selected-condition",
  SET_SELECTED_VARIANT = "set-selected-variant",
  SET_SELECTED_SLOT = "set-selected-slot",
  SET_SELECTED_SLOT_STATUS = "set-selected-slot-status",
  UPDATE_CURRENT_VIEW = "update-current-view",
  UPDATE_HAS_UNSAVED_CHANGES = "update-has-unsaved-changes",
}

export function areEqualArray(arr1: number[] | string[], arr2: number[] | string[]) {
  if (arr1.length !== arr2.length) return false;

  let c1 = arr1.slice().sort();
  let c2 = arr2.slice().sort();

  for (let i = 0; i < c1.length; i++) {
    if (c1[i] !== c2[i]) return false;
  }

  return true;
}

export function dateDifferenceInDays(date1: Date, date2: Date) {
  const startDate = dayjs(date1);
  const endDate = dayjs(date2);

  return endDate.diff(startDate, "day");
}

export function dateFormatYYYYMMDD(date: Date) {
  return dayjs(date).format("YYYY-MM-DD");
}

export function subtractDaysAndFormat(daysToSubtract: number, format = "YYYY-MM-DD", dateString?: string | null) {
  const date = dateString ? dayjs(dateString) : dayjs();

  const subtractedDate = date.subtract(daysToSubtract, "day");

  const formattedDate = subtractedDate.format(format);

  return formattedDate;
}

export function calculatePercentageDifference(currentValue: number | string, previousValue: number | string): string {
  const current = +currentValue;
  const previous = +previousValue;

  if (previous === 0) {
    return current === 0 ? "0%" : "100%";
  }

  const percentageDifference = ((current - previous) / previous) * 100;
  const roundedPercentage = percentageDifference.toFixed(0);

  return roundedPercentage + "%";
}

export function calculateDifferences(currentItem: AnalyticsRecord, previousItem: AnalyticsRecord): AnalyticsDiff {
  return {
    impressionsDiffPercentage: String(calculatePercentageDifference(currentItem.impressions, previousItem.impressions)),
    clicksDiffPercentage: String(calculatePercentageDifference(currentItem.clicks, previousItem.clicks)),
    conversionsDiffPercentage: String(calculatePercentageDifference(currentItem.conversions, previousItem.conversions)),
    revenueDiffPercentage: String(calculatePercentageDifference(currentItem.revenue, previousItem.revenue)),
    cvrDiffPercentage:
      previousItem.cvr !== 0
        ? String((((currentItem.cvr - previousItem.cvr) / Math.abs(previousItem.cvr)) * 100).toFixed(0)) + "%"
        : "100%",
    ctrDiffPercentage: String(calculatePercentageDifference(currentItem.ctr, previousItem.ctr)),
    declinedDiffPercentage: String(calculatePercentageDifference(currentItem?.declined, previousItem.declined)),
  };
}

export const removeDecimals = (obj: AnalyticsRecord): AnalyticsRecord => {
  const newObj: AnalyticsRecord = { ...obj };

  for (let key in newObj) {
    if (typeof newObj[key] === "number") {
      newObj[key] = Math.floor(newObj[key] as number);
    }
  }
  return { ...newObj, revenue: +obj?.revenue?.toFixed(2), cvr: +obj?.cvr?.toFixed(2) };
};

export const getCalculatedData = (current: AnalyticsRecord[], previous: AnalyticsRecord[]): AnalyticsRecord[] => {
  if (!current.length && !previous.length) {
    return [];
  }

  if (current.length === 0) {
    let currentData = previous.map((currentItem: AnalyticsRecord, index: number) => ({
      ...currentItem,
      ctr: 0,
      cvr: 0,
      revenue: 0,
      conversions: 0,
      clicks: 0,
      impressions: 0,
      declined: 0,
      impressionsDiffPercentage: "-100%",
      clicksDiffPercentage: "-100%",
      conversionsDiffPercentage: "-100%",
      revenueDiffPercentage: "-100%",
      cvrDiffPercentage: "-100%",
      ctrDiffPercentage: "-100%",
      declinedDiffPercentage: "-100%",
    }));

    return currentData;
  }

  if (previous.length === 0) {
    let previousData = current.map((currentItem: AnalyticsRecord, index: number) => ({
      ...currentItem,
      cvr: currentItem.cvr,
      impressionsDiffPercentage: "100%",
      clicksDiffPercentage: "100%",
      conversionsDiffPercentage: "100%",
      revenueDiffPercentage: "100%",
      cvrDiffPercentage: "100%",
      ctrDiffPercentage: "100%",
      declinedDiffPercentage: "100%",
    }));

    return previousData.map(removeDecimals);
  }

  const resultWithPercentage = current.map(currentItem => {
    const previousItem = previous.find(prevItem => prevItem.baseId === currentItem.baseId);

    if (previousItem) {
      return {
        ...currentItem,
        ...calculateDifferences(currentItem, previousItem),
      };
    } else {
      return currentItem;
    }
  });

  return resultWithPercentage.map(removeDecimals);
};

export const removeHtmlElementsFromString = (val: string) => {
  let temporaryElement = document.createElement("div");

  temporaryElement.innerHTML = val;
  return temporaryElement.textContent || temporaryElement.innerText || "";
};

export const getDiscountText = (t: TFunction, value: number, type: string, isSubscriptionEnabled?: boolean): string => {
  const suffix = isSubscriptionEnabled ? ` (${t("for one-time purchase")})` : "";

  if (value) {
    if (type === "PERCENTAGE") {
      return `${value}% ${t("discount")}${suffix}`;
    } else {
      return `${value} ${t("discount")}${suffix}`;
    }
  } else {
    return `${t("No discount")}${suffix}`;
  }
};

export const handleRedirectDashboard = () => {
  try {
    const redirectUrl = sessionStorage.getItem("redirectUrl");

    if (redirectUrl) {
      window.open(redirectUrl, "_blank");
    }
  } catch (error) {
    console.log(error);
  }
};


interface BreadcrumbFormatParams {
  page?: string;
  condition?: string;
  slot?: string;
  variant?: string;
}

export const generateBreadcrumbFormat = ({ page, condition, slot, variant }: BreadcrumbFormatParams) => {
  console.log("Values to generate breadcrumb", page, condition, slot, variant);
  return [t(PageNames[page as PageType] ?? ""), condition, slot, variant].filter(Boolean).join(" > ");
};

export const sendPostMessage = (event: IFramePostMessageType, data?: any, otherData?: any) => {
  if (window?.shopify) {
    if (window?.top?.frames) {
      for (let i = 0; i < window.top.frames.length; i++) {
        try {
          window.top.frames[i].postMessage({
            eventType: event,
            data,
            ...otherData,
          }, "*");
        } catch (error) {
          console.warn(`Failed to send to frame ${i}:`, error);
        }
      }
    }
  } else {
    window?.top?.postMessage({
      eventType: event,
      data,
      ...otherData,
    }, "*");
  }
};

export const getNextVariantName = (currentVariant: string) => {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const currentLetter = currentVariant.charAt(currentVariant.length - 1);
  const currentLetterIndex = alphabet.indexOf(currentLetter);

  if (currentLetterIndex === -1) {
    throw new Error("Invalid variant name format. Expected format: 'Variant A', 'Variant B', etc.");
  }

  const nextLetterIndex = (currentLetterIndex + 1) % alphabet.length;
  const nextLetter = alphabet.charAt(nextLetterIndex);

  return `Variant ${nextLetter}`;
};

export function updateAbImpressionShares(variantId: string, newValue: number, variants: any[]) {
  if (newValue < 0 || newValue > 100) {
    throw new Error("abImpressionShare must be between 0 and 100.");
  }

  const updatedVariants = [...variants];
  const variantIndex = updatedVariants.findIndex(variant => variant.id === variantId);

  if (variantIndex === -1) {
    throw new Error("Variant not found.");
  }

  // Update the selected variant's abImpressionShare
  updatedVariants[variantIndex].abImpressionShare = newValue;

  if (updatedVariants.length === 2) {
    // Calculate remaining allocation
    const remainingAllocation = 100 - newValue;
    const otherVariants = updatedVariants.filter(variant => variant.id !== variantId);

    let totalShares = newValue;

    // Update other variants' abImpressionShare proportionally
    otherVariants.forEach((variant, index) => {
      const proportionalShare = Math.round(((variant.abImpressionShare ?? 1) / 100) * remainingAllocation);

      if (index === otherVariants.length - 1) {
        // Adjust the last variant's share to ensure total is 100
        variant.abImpressionShare = 100 - totalShares;
      } else {
        variant.abImpressionShare = proportionalShare;
      }

      totalShares += variant.abImpressionShare;
    });
  } else {
    const sumOfAllShares = updatedVariants.reduce((acc, current) => acc + current.abImpressionShare, 0);

    if (sumOfAllShares !== 100) {
      return { updated: updatedVariants, isError: true };
    }
  }

  return { updated: updatedVariants, isError: false };
}

export const compressImage = (imageUrl: string, width: number) => { // Load product images with the correct size
  if (!imageUrl) {
    return imageUrl;
  }

  const lastDotIndex = imageUrl.lastIndexOf('.');

  if (lastDotIndex > -1) {
    const basePart = imageUrl.substring(0, lastDotIndex);
    const extensionPart = imageUrl.substring(lastDotIndex);

    return `${basePart}_${width}x${width}${extensionPart}`;
  }

  return imageUrl;
};

export const compressImageForWix = (imageUrl: string, width: number, quality: number = 100) => {
  if (!imageUrl) return imageUrl;

  const lastSlashIndex = imageUrl.lastIndexOf("/");

  if (lastSlashIndex === -1) return imageUrl;

  // Split into file name
  const fileName = imageUrl.substring(lastSlashIndex + 1);

  // Insert Wix transformation string
  return `${imageUrl}/v1/fill/w_${width},h_${width},q_${quality}/${fileName}`;
};

export const getDifferenceInDays = (oldDate: Date, newDate: Date) => {
  const PER_DAY = 1000 * 60 * 60 * 24;

  // Discard the time and time-zone information.
  const utc1 = Date.UTC(
    oldDate.getFullYear(),
    oldDate.getMonth(),
    oldDate.getDate()
  );

  const utc2 = Date.UTC(
    newDate.getFullYear(),
    newDate.getMonth(),
    newDate.getDate()
  );

  return Math.floor((utc2 - utc1) / PER_DAY);
};

export const calculateDaysOfUsage = (install_date: string) => {
  if (install_date) {
    const firstDateOfAppUsage = new Date(install_date);

    return getDifferenceInDays(firstDateOfAppUsage, new Date()) + 1;
  }

  return 1;
};

export const decodeFbqParam = (fbq: string): Record<string, string> => {
  try {
    const decoded = atob(fbq); // Base64 decode
    const entries = decoded.split("&").map(part => {
      const [key, value] = part.split("=");

      return [key, decodeURIComponent(value)];
    });

    return Object.fromEntries(entries);
  } catch (error) {
    console.error("Failed to decode fbq param:", error);
    return {};
  }
};

export const isOlderThan24Hours = (dateString: string) => {
  const date = moment(dateString).utc();
  const now = moment().utc();

  return now.diff(date, 'hours') > 23;
};

export const handleCapitalize = (str: string): string => {
  if (!str) return "";

  return str
    .split("_")
    .map((word, index) => {
      const lowerWord = word.toLowerCase();
      // Capitalize only the first word, keep rest lowercase

      if (index === 0) {
        return lowerWord.charAt(0).toUpperCase() + lowerWord.slice(1);
      }

      return lowerWord;
    })
    .join(" ")?.trim();
};

